import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil, Plus, Trash2, UserMinus, UserPlus, UsersRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMe } from '~/api/auth';
import { departmentsQueryOptions } from '~/api/people';
import { useServerClock } from '~/api/serverClock';
import {
  type Team,
  type TeamPerson,
  teamCandidatesQueryOptions,
  teamsQueryOptions,
  useAddTeamMembers,
  useCreateTeam,
  useDeleteTeam,
  useRemoveTeamMember,
  useUpdateTeam,
} from '~/api/teams';
import { listsQueryOptions } from '~/api/work';
import { OccurrenceSheet } from '~/features/work/OccurrenceSheet';
import { TaskSheet } from '~/features/work/TaskSheet';
import { calendarDate } from '~/lib/format';
import { notify } from '~/lib/notify';
import { Avatar } from '~/ui/Avatar';
import { Button } from '~/ui/Button';
import { Card } from '~/ui/Card';
import { useConfirm } from '~/ui/Confirm';
import { Dialog } from '~/ui/Dialog';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Input } from '~/ui/Input';
import { PageHeader } from '~/ui/PageHeader';
import { Select } from '~/ui/Select';
import { Skeleton } from '~/ui/Skeleton';

interface Search {
  task?: string;
  run?: string;
  owner?: string;
}

export const Route = createFileRoute('/_app/teams')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    task: typeof s['task'] === 'string' && s['task'] ? s['task'] : undefined,
    run: typeof s['run'] === 'string' && s['run'] ? s['run'] : undefined,
    owner: typeof s['owner'] === 'string' && s['owner'] ? s['owner'] : undefined,
  }),
  component: TeamsPage,
});

/**
 * Teams inside a department, each led by a sub manager. A sub manager makes their own
 * from the members of their department and hands them work from here; the department's
 * manager and the owner change any team and give it to another sub manager.
 */
function TeamsPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/teams' });
  const now = useServerClock(60_000);
  const today = calendarDate(now, me.person.tz ?? me.workspace.tz);
  const go = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const teams = useQuery(teamsQueryOptions);
  const lists = useQuery(listsQueryOptions);
  const [making, setMaking] = useState(false);
  const [editing, setEditing] = useState<Team | null>(null);
  const [adding, setAdding] = useState<Team | null>(null);
  const isSub = me.person.role === 'sub_manager';

  const subtitle = isSub
    ? 'Your teams: hand them work from here and review it as it comes in.'
    : 'The teams in your company, each led by a sub manager.';

  return (
    <div className="mx-auto max-w-[1100px]">
      <PageHeader
        eyebrow="People"
        title="Teams"
        subtitle={subtitle}
        actions={
          <Button icon={<Plus size={16} aria-hidden />} onClick={() => setMaking(true)}>
            New team
          </Button>
        }
      />

      {teams.isPending ? (
        <Skeleton lines={6} />
      ) : teams.isError ? (
        <ErrorNotice error={teams.error} onRetry={() => void teams.refetch()} />
      ) : teams.data.length === 0 ? (
        <EmptyState
          icon={<UsersRound />}
          title={isSub ? 'You have no team yet.' : 'There are no teams yet.'}
          body={
            isSub
              ? 'Make one from the members of your department, then give them work.'
              : 'A sub manager makes teams from their department. You can make one for them too.'
          }
          action={<Button onClick={() => setMaking(true)}>Make a team</Button>}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {teams.data.map((t) => (
            <TeamCard
              key={t.id}
              team={t}
              onGive={(personId) => go({ task: 'new', owner: personId })}
              onEdit={() => setEditing(t)}
              onAdd={() => setAdding(t)}
            />
          ))}
        </div>
      )}

      {/* Mounted only while open, so each opening starts from the team as it is. */}
      {making && <TeamDialog onClose={() => setMaking(false)} />}
      {editing && <TeamDialog team={editing} onClose={() => setEditing(null)} />}
      {adding && <AddPeopleDialog team={adding} onClose={() => setAdding(null)} />}

      <TaskSheet
        id={search.task ?? null}
        newOwnerId={search.owner}
        lists={lists.data ?? []}
        today={today}
        onClose={() => go({ task: undefined, owner: undefined })}
        onOpenRun={(id) => go({ task: undefined, run: id })}
        onOpenTask={(id) => go({ task: id, owner: undefined })}
      />
      <OccurrenceSheet
        id={search.run ?? null}
        today={today}
        onClose={() => go({ run: undefined })}
        onOpenTask={(id) => go({ run: undefined, task: id })}
      />
    </div>
  );
}

function TeamCard({
  team: t,
  onGive,
  onEdit,
  onAdd,
}: {
  team: Team;
  onGive: (personId: string) => void;
  onEdit: () => void;
  onAdd: () => void;
}) {
  const me = useMe();
  const confirm = useConfirm();
  const remove = useRemoveTeamMember();
  const drop = useDeleteTeam();
  // The lead, the department's manager and the owner hand out work to the team.
  const canGive = t.canManage;

  const onRemove = async (p: TeamPerson) => {
    if (
      !(await confirm({
        title: `Take ${p.name} off ${t.name}?`,
        body: 'Their work stays as it is. The team’s sub manager no longer hands them work.',
        confirmLabel: 'Take off',
        tone: 'danger',
      }))
    )
      return;
    try {
      await remove.mutateAsync({ id: t.id, personId: p.id });
    } catch (e) {
      notify.error('Could not take them off the team.', e);
    }
  };

  const onDelete = async () => {
    if (
      !(await confirm({
        title: `Delete ${t.name}?`,
        body: 'Its people and their work stay. Only the team goes.',
        confirmLabel: 'Delete',
        tone: 'danger',
      }))
    )
      return;
    try {
      await drop.mutateAsync(t.id);
      notify.success(`${t.name} is deleted.`);
    } catch (e) {
      notify.error('Could not delete the team.', e);
    }
  };

  return (
    <Card
      eyebrow={t.departmentName}
      title={t.name}
      actions={
        t.canManage ? (
          <div className="flex gap-1">
            <Button size="sm" variant="quiet" aria-label={`Edit ${t.name}`} onClick={onEdit}>
              <Pencil size={15} aria-hidden />
            </Button>
            <Button size="sm" variant="quiet" aria-label={`Delete ${t.name}`} onClick={onDelete}>
              <Trash2 size={15} aria-hidden />
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="mb-3 flex items-center gap-2 text-sm">
        <Avatar name={t.lead.name} personId={t.lead.id} photoId={t.lead.avatarFileId} size="sm" />
        <span className="min-w-0">
          <span className="block truncate font-medium">
            {t.lead.name}
            {t.lead.id === me.person.id && <span className="font-normal text-ink-3"> (you)</span>}
          </span>
          <span className="block text-xs text-ink-3">Sub manager</span>
        </span>
      </div>

      {t.members.length === 0 ? (
        <p className="rounded-control bg-paper-2 px-3 py-2 text-sm text-ink-2">
          Nobody on this team yet.
        </p>
      ) : (
        <ul className="divide-y divide-line rounded-control border border-line">
          {t.members.map((p) => (
            <li key={p.id} className="flex items-center gap-2 px-3 py-2">
              <Avatar name={p.name} personId={p.id} photoId={p.avatarFileId} size="sm" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{p.name}</span>
                {p.title && <span className="block truncate text-xs text-ink-3">{p.title}</span>}
              </span>
              {canGive && (
                <Button size="sm" variant="soft" onClick={() => onGive(p.id)}>
                  Give a task
                </Button>
              )}
              {t.canManage && (
                <Button
                  size="sm"
                  variant="quiet"
                  aria-label={`Take ${p.name} off the team`}
                  onClick={() => void onRemove(p)}
                >
                  <UserMinus size={15} aria-hidden />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {t.canManage && (
        <div className="mt-3">
          <Button
            size="sm"
            variant="secondary"
            icon={<UserPlus size={15} aria-hidden />}
            onClick={onAdd}
          >
            Add people
          </Button>
        </div>
      )}
    </Card>
  );
}

/** Ticks for people, for picking several at once. */
function PeopleTicks({
  people,
  picked,
  onChange,
  empty,
}: {
  people: TeamPerson[];
  picked: string[];
  onChange: (ids: string[]) => void;
  empty: string;
}) {
  if (people.length === 0) return <p className="text-sm text-ink-3">{empty}</p>;
  return (
    <ul className="max-h-64 divide-y divide-line overflow-y-auto rounded-control border border-line">
      {people.map((p) => {
        const on = picked.includes(p.id);
        return (
          <li key={p.id}>
            <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-paper-2">
              <input
                type="checkbox"
                checked={on}
                onChange={() => onChange(on ? picked.filter((x) => x !== p.id) : [...picked, p.id])}
                className="size-4 accent-(--accent)"
              />
              <Avatar name={p.name} personId={p.id} photoId={p.avatarFileId} size="xs" />
              <span className="min-w-0 flex-1 truncate">{p.name}</span>
              {p.title && <span className="truncate text-xs text-ink-3">{p.title}</span>}
            </label>
          </li>
        );
      })}
    </ul>
  );
}

/** Make a team, or rename one and (for a manager or the owner) give it another sub manager. */
function TeamDialog({ team, onClose }: { team?: Team; onClose: () => void }) {
  const me = useMe();
  const isOwner = me.person.role === 'owner';
  const isSub = me.person.role === 'sub_manager';
  const editing = Boolean(team);
  const [name, setName] = useState(team?.name ?? '');
  const [departmentId, setDepartmentId] = useState(team?.departmentId ?? '');
  const [leadId, setLeadId] = useState(team?.lead.id ?? '');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const create = useCreateTeam();
  const update = useUpdateTeam();

  const departments = useQuery({
    ...departmentsQueryOptions,
    enabled: isOwner && !editing,
  });
  const dept = editing ? team!.departmentId : isOwner ? departmentId || undefined : undefined;
  const candidates = useQuery({
    ...teamCandidatesQueryOptions(isOwner ? dept : undefined),
    enabled: !isOwner || Boolean(dept),
  });
  const leads = candidates.data?.leads ?? [];
  const members = (candidates.data?.members ?? []).filter((p) => p.id !== leadId);
  const pickLead = !isSub && (!editing || team!.canChangeLead);

  const submit = async () => {
    try {
      if (team) {
        await update.mutateAsync({
          id: team.id,
          ...(name.trim() !== team.name ? { name: name.trim() } : {}),
          ...(pickLead && leadId && leadId !== team.lead.id ? { leadPersonId: leadId } : {}),
        });
        notify.success('Team saved.');
      } else {
        await create.mutateAsync({
          name: name.trim(),
          ...(isSub ? {} : { leadPersonId: leadId }),
          memberIds,
        });
        notify.success(`${name.trim()} is ready. Give its people work from here.`);
      }
      onClose();
    } catch (e) {
      notify.error(team ? 'Could not save the team.' : 'Could not make the team.', e);
    }
  };

  const ready = name.trim().length >= 2 && (isSub || editing || Boolean(leadId));

  return (
    <Dialog
      open
      onClose={onClose}
      title={team ? `Edit ${team.name}` : 'New team'}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={create.isPending || update.isPending}
            disabled={!ready}
            onClick={() => void submit()}
          >
            {team ? 'Save' : 'Make the team'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          placeholder="Dispatch, Night shift…"
          autoFocus
        />
        {isOwner && !editing && (
          <Select
            label="Department"
            value={departmentId}
            onChange={(e) => {
              setDepartmentId(e.target.value);
              setLeadId('');
              setMemberIds([]);
            }}
          >
            <option value="">Choose a department…</option>
            {(departments.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        )}
        {pickLead && (
          <Select
            label="Sub manager"
            value={leadId}
            onChange={(e) => setLeadId(e.target.value)}
            hint={
              leads.length === 0 && !candidates.isPending
                ? 'Nobody in this department is a sub manager yet. Make someone one on their page under Team.'
                : 'They lead the team: hand out its work and review it.'
            }
          >
            <option value="">Choose…</option>
            {leads.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        )}
        {!editing && (isSub || Boolean(dept) || !isOwner) && (
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium">
              People <span className="font-normal text-ink-3">(you can add more later)</span>
            </p>
            <PeopleTicks
              people={members}
              picked={memberIds}
              onChange={setMemberIds}
              empty="There are no members in this department to add."
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}

function AddPeopleDialog({ team, onClose }: { team: Team; onClose: () => void }) {
  const me = useMe();
  const [picked, setPicked] = useState<string[]>([]);
  const add = useAddTeamMembers();
  const candidates = useQuery({
    ...teamCandidatesQueryOptions(me.person.role === 'owner' ? team?.departmentId : undefined),
  });
  const free = useMemo(() => {
    const on = new Set(team.members.map((m) => m.id));
    return (candidates.data?.members ?? []).filter((p) => !on.has(p.id) && p.id !== team.lead.id);
  }, [candidates.data, team]);

  const submit = async () => {
    try {
      await add.mutateAsync({ id: team.id, personIds: picked });
      notify.success(
        picked.length === 1 ? 'Added to the team.' : `${picked.length} added to the team.`,
      );
      onClose();
    } catch (e) {
      notify.error('Could not add them.', e);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Add people to ${team.name}`}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={add.isPending}
            disabled={picked.length === 0}
            onClick={() => void submit()}
          >
            Add {picked.length > 0 ? picked.length : ''}
          </Button>
        </>
      }
    >
      {candidates.isPending ? (
        <Skeleton lines={4} />
      ) : (
        <PeopleTicks
          people={free}
          picked={picked}
          onChange={setPicked}
          empty={`Everyone in ${team.departmentName} is already on this team.`}
        />
      )}
    </Dialog>
  );
}
